import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { AccountProvider } from "./lib/account";
import { ThemeProvider } from "./lib/theme";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Trades from "./pages/Trades";
import TradeDetail from "./pages/TradeDetail";
import Analytics from "./pages/Analytics";
import Charts from "./pages/Charts";
import Billing from "./pages/Billing";
import Settings from "./pages/Settings";
import BrokersSettings from "./pages/BrokersSettings";
import Admin from "./pages/Admin";
import Profile from "./pages/Profile";
import SharedPnL from "./pages/SharedPnL";
import SharedStrategy from "./pages/SharedStrategy";
import SharedPropChallenge from "./pages/SharedPropChallenge";
import PropFirm from "./pages/PropFirm";
import PropChallenge from "./pages/PropChallenge";
import Certificates from "./pages/Certificates";
import Strategies from "./pages/Strategies";
import { BlogIndex, BlogPost } from "./pages/Blog";
import VsPage from "./pages/VsPage";
import Layout from "./components/Layout";
import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  // Google OAuth returns to `${origin}/dashboard#session_id=...`.
  // Intercept the fragment synchronously (not in useEffect) so the callback
  // handler runs BEFORE the Protected route redirects to /login.
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      {/* Regional landings — hreflang-linked, region-tuned SEO + auto
          currency default. All render the same Landing component under
          the hood; only the SEO envelope and default currency differ. */}
      <Route path="/in"  element={<Landing region="in"  />} />
      <Route path="/us"  element={<Landing region="us"  />} />
      <Route path="/uk"  element={<Landing region="uk"  />} />
      <Route path="/eu"  element={<Landing region="eu"  />} />
      <Route path="/sea" element={<Landing region="sea" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/shared/:token" element={<SharedPnL />} />
      <Route path="/strategy/:token" element={<SharedStrategy />} />
      <Route path="/prop-challenge/share/:token" element={<SharedPropChallenge />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/vs/:slug" element={<VsPage />} />
      <Route element={<Protected><AccountProvider><Layout /></AccountProvider></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/trades" element={<Trades />} />
        <Route path="/trades/:id" element={<TradeDetail />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/strategies" element={<Strategies />} />
        <Route path="/prop" element={<PropFirm />} />
        <Route path="/prop-challenge" element={<PropChallenge />} />
        <Route path="/certificates" element={<Certificates />} />
        <Route path="/charts" element={<Charts />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings/brokers" element={<BrokersSettings />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App grain">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
        <Toaster position="top-right" richColors />
      </ThemeProvider>
    </div>
  );
}
